// src/components/Profile/Profile.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
// Correct import for loadStripe:
import { loadStripe } from '@stripe/stripe-js';
import axiosInstance from '../../utils/axiosInstance';
import { useAuth } from '../../hooks/useAuth';
import DogCard from './DogCard/DogCard';
import AddDogModal from './AddDogModal/AddDogModal';
import EditDogModal from './DogCard/EditDogModal';
import VaccineManagerModal from './DogCard/VaccineManagerModal';
import MembershipAndVisits from './MembershipAndVisits/MembershipAndVisits';
import UserInfo from './UserInfo/UserInfo';
import SignedWaiversDisplay from './WaiverStatus/WaiverStatus';
import MakeRecurringMembershipModal from './MembershipAndVisits/MakeRecurringMembershipModal/MakeRecurringMembershipModal';
import CancelRecurringMembershipModal from './MembershipAndVisits/CancelRecurringMembershipModal/CancelRecurringMembershipModal';
import SaveRecurringCardModal from './MembershipAndVisits/SaveRecurringCardModal/SaveRecurringCardModal';
import CancelPendingCancellationModal from './MembershipAndVisits/CancelPendingCancellationModal/CancelPendingCancellationModal';
import './Profile.css';
import LoadingHelper from '../../helpers/LoadingHelper/loadingHelper';

const stripePromise = (() => {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!key) {
    console.error('Missing Stripe publishable key. Please check your environment variables.');
    return null;
  }
  return loadStripe(key);
})();

function Profile() {
  const { isAuthenticated, isLoading } = useAuth();
  const [user, setUser] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [cardDetails, setCardDetails] = useState(null);

  // Show/hide modals
  const [showAddDogModal, setShowAddDogModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showMakeRecurringModal, setShowMakeRecurringModal] = useState(false);
  const [showSaveRecurringCardModal, setShowSaveRecurringCardModal] = useState(false);
  const [showConfirmCancelPendingModal, setShowConfirmCancelPendingModal] = useState(false);

  // Entities
  const [editableDog, setEditableDog] = useState(null);
  const [vaccineModalDog, setVaccineModalDog] = useState(null);
  const [membershipToCancel, setMembershipToCancel] = useState(null);
  const [membershipToMakeRecurring, setMembershipToMakeRecurring] = useState(null);

  // For reloading user data
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FETCH USER PROFILE
  const fetchUserProfile = async () => {
    const token = localStorage.getItem('token');
    if (token && isAuthenticated) {
      try {
        const response = await axiosInstance.get('/users/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
        setDogs(response.data.dogs);
        setCardDetails(response.data.cardDetails);
      } catch (error) {
        if (error.response && error.response.status === 403) {
          navigate('/login');
        }
        console.error('Failed to fetch user profile', error);
      }
    } else {
      navigate('/login');
    }
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      fetchUserProfile();
    }
    // eslint-disable-next-line
  }, [isAuthenticated, isLoading, refreshTrigger]);

  if (isLoading) return <LoadingHelper />;
  if (!user) return <div><br /><br />Can't load user profile... refresh your login</div>;

  // CANCEL RECURRING LOGIC
  const handleCancelRecurringClicked = (membership) => {
    setMembershipToCancel(membership);
    setShowCancelModal(true);
  };

  async function handleCancelRecurringConfirmed() {
    if (!membershipToCancel) return;
    setShowCancelModal(false);
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.delete(
        `/membership/${membershipToCancel._id}/cancel`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRefreshTrigger(prev => !prev);
    } catch (err) {
      console.error('Error canceling membership:', err);
    } finally {
      setMembershipToCancel(null);
    }
  }

  const handleCancelPendingCancellationClicked = (membership) => {
    setMembershipToCancel(membership);
    setShowConfirmCancelPendingModal(true);
  };

  const handleConfirmCancelPendingCancellation = async () => {
    if (!membershipToCancel) return;
    setShowConfirmCancelPendingModal(false);
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.post(
        `/membership/${membershipToCancel._id}/cancelPending`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRefreshTrigger(prev => !prev);
    } catch (err) {
      console.error('Error canceling pending cancellation:', err);
    } finally {
      setMembershipToCancel(null);
    }
  };

  // MAKE RECURRING LOGIC
  const handleMakeRecurringClicked = (membership) => {
    if (!user?.stripeCardId && !cardDetails) {
      setMembershipToMakeRecurring(membership);
      setShowSaveRecurringCardModal(true);
    } else {
      setMembershipToMakeRecurring(membership);
      setShowMakeRecurringModal(true);
    }
  };

  async function handleMakeRecurringConfirmed() {
    if (!membershipToMakeRecurring) return;
    setShowMakeRecurringModal(false);
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.post(
        `/membership/${membershipToMakeRecurring._id}/makeRecurring`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update card details if they were updated during the recurring process
      if (response.data.cardDetails) {
        setCardDetails(response.data.cardDetails);
      }

      // Update the membership in the user's membershipDetails
      if (user && user.membershipDetails) {
        const updatedMembershipDetails = user.membershipDetails.map(m => 
          m._id === membershipToMakeRecurring._id 
            ? { 
                ...m, 
                isRecurring: true, 
                cancelPending: false,
                nextPaymentDate: response.data.nextPaymentDate || m.nextPaymentDate
              }
            : m
        );
        setUser(prev => ({
          ...prev,
          membershipDetails: updatedMembershipDetails
        }));
      }
      
      // Update the dogs array if it contains membership information
      if (dogs.length > 0) {
        const updatedDogs = dogs.map(dog => ({
          ...dog,
          memberships: dog.memberships?.map(m => 
            m._id === membershipToMakeRecurring._id
              ? { 
                  ...m, 
                  isRecurring: true, 
                  cancelPending: false,
                  nextPaymentDate: response.data.nextPaymentDate || m.nextPaymentDate
                }
              : m
          )
        }));
        setDogs(updatedDogs);
      }
      
      setRefreshTrigger(prev => !prev);
    } catch (err) {
      console.error('Error making membership recurring:', err);
    } finally {
      setMembershipToMakeRecurring(null);
    }
  }

  // Called once the user saved a card in SaveRecurringCardModal
  const handleCardSaved = () => {
    setRefreshTrigger(prev => !prev);
    setShowSaveRecurringCardModal(false);
    setShowMakeRecurringModal(true);
  };

  // DOG / VACCINE
  const handleEditDog = (dog) => setEditableDog(dog);
  const handleOpenVaccineModal = (dog) => setVaccineModalDog(dog);
  const handleCloseVaccineModal = () => setVaccineModalDog(null);

  return (
    <>
      <div className="profile-page-container">
        <div className="profile-columns">
          <div className="profile-column">
            <MembershipAndVisits
              activeMembershipCount={user.activeMembershipCount}
              totalDayPasses={user.totalUnusedDayPasses}
              checkInCount={dogs.reduce((total, dog) => total + (dog.totalVisits || 0), 0)}
              firstVisitDate={user.firstVisitDate}
              membershipDetails={user.membershipDetails}
              membershipCounts={user.membershipCounts}
              onCancelRecurringClicked={handleCancelRecurringClicked}
              onMakeRecurringClicked={handleMakeRecurringClicked}
              onCancelPendingCancellationClicked={handleCancelPendingCancellationClicked}
              user={user}
              cardDetails={cardDetails}
              setMembershipDetails={(updatedDetails) => {
                setUser(prev => ({
                  ...prev,
                  membershipDetails: updatedDetails
                }));
              }}
            />
          </div>

          <div className="profile-column">
            <div className="center-content">
              <div className="dogs-title">Your Dogs</div>
              <button className="add-dog-button" onClick={() => setShowAddDogModal(true)}>
                Add Dog
              </button>

              {showAddDogModal && (
                <AddDogModal
                  onCancel={() => setShowAddDogModal(false)}
                  userId={user?._id}
                  setRefreshTrigger={setRefreshTrigger}
                />
              )}

              <div className="dogs">
                {dogs.map(d => (
                  <DogCard
                    key={d._id}
                    dog={d}
                    onEdit={() => handleEditDog(d)}
                    onManageVaccinations={() => handleOpenVaccineModal(d)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="profile-column">
            <Elements stripe={stripePromise}>
              <UserInfo
                userId={user._id}
                name={user.name}
                email={user.email}
                phone={user.phoneNumber}
                setRefreshTrigger={setRefreshTrigger}
                onCardDetailsUpdate={setCardDetails}
              />

              <SignedWaiversDisplay user={user} />

              {showSaveRecurringCardModal && (
                <SaveRecurringCardModal
                  isOpen={showSaveRecurringCardModal}
                  onClose={() => setShowSaveRecurringCardModal(false)}
                  onCardSaved={handleCardSaved}
                />
              )}
            </Elements>
          </div>
        </div>
      </div>

      <div
        style={{
          aspectRatio: '960/100',
          backgroundImage: "url('/blueToPinkProfile.png')",
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      ></div>

      {showCancelModal && (
        <CancelRecurringMembershipModal
          isOpen={showCancelModal}
          membership={membershipToCancel}
          onConfirm={handleCancelRecurringConfirmed}
          onCancel={() => {
            setShowCancelModal(false);
            setMembershipToCancel(null);
          }}
        />
      )}

      {showMakeRecurringModal && stripePromise && (
        <Elements stripe={stripePromise}>
          <MakeRecurringMembershipModal
            isOpen={showMakeRecurringModal}
            membership={membershipToMakeRecurring}
            onConfirm={handleMakeRecurringConfirmed}
            onCancel={() => {
              setShowMakeRecurringModal(false);
              setMembershipToMakeRecurring(null);
            }}
            user={user}
            cardDetails={cardDetails}
          />
        </Elements>
      )}

      {showConfirmCancelPendingModal && (
        <CancelPendingCancellationModal
          isOpen={showConfirmCancelPendingModal}
          onConfirm={handleConfirmCancelPendingCancellation}
          onCancel={() => setShowConfirmCancelPendingModal(false)}
        />
      )}

      {editableDog && (
        <EditDogModal
          dog={editableDog}
          onCancel={() => setEditableDog(null)}
          userId={user?._id}
          setRefreshTrigger={setRefreshTrigger}
        />
      )}

      {vaccineModalDog && (
        <VaccineManagerModal
          isOpen={vaccineModalDog !== null}
          onClose={handleCloseVaccineModal}
          dog={vaccineModalDog}
        />
      )}
    </>
  );
}

export default Profile;
